import {
  ChangeDetectionStrategy,
  Component,
  ChangeDetectorRef,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  inject,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { scrollParent } from '../load-more.directive';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatCheckboxDefaultOptions, MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckboxModule } from '@angular/material/checkbox';
import { Sort, MatSortModule } from '@angular/material/sort';
import { FormatCellPipe } from '../pipes/format-cell-pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MatTableModule } from '@angular/material/table';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { BreakpointService } from 'src/app/services/breakpoint.service';

export type TableCell = {
  iconLeading?: string;
  linkCallback?: boolean;
  menus?: { value: string; label: string }[];
  routerLink?: string;
  tooltipText?: string;
  value?: string | number | Date;
  value2?: string | number | undefined;
};

export type TableRow = Record<string, TableCell>;

export interface TableColumn {
  key: string;
  name: string;
  indicatorIcon?: boolean;
  isSortable?: boolean;
  isSticky?: boolean;
  /** phoneLayout="rows": start this column on a new line. */
  breakBefore?: boolean;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
    },
  ],
  imports: [
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    NgClass,
    NgTemplateOutlet,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    AngularSvgIconModule,
    MatTooltipModule,
    MatPaginatorModule,
    TranslatePipe,
    FormatCellPipe,
  ],
})
export class TableComponent implements OnChanges {
  @Input() bulkEnabled = false;
  @Input() set dataSource(tableRows: TableRow[]) {
    const rows = tableRows || [];
    // A phone list that grows: the parent hands over the same rows plus the next page, so the selection stays valid.
    const grown = this.infiniteScroll && rows.length > this._dataSource.length && this.sameStart(rows);
    this._dataSource = rows;
    this.loadingMore = false;
    this.loadFailed = false;
    // After the new rows are in the page, so the observer root is picked from the final layout.
    requestAnimationFrame(() => this.observe());
    if (!grown) {
      this.selectedIndex = -1;
      this.selectedItems.clear();
    }
    this.setSelectedStates();
  }
  @Input() pageSize = 50;
  @Input() pageSizeOptions: number[] = [50, 100, 150, 200];
  @Input() selectedPageIndex = 1;
  @Input() showSum? = false;
  /** Translation key for the count label shown above the table when `showSum` is on. */
  @Input() sumLabel = 'SHARED.TABLE.RESULTS';
  @Input() set tableCols(tableCols: TableColumn[]) {
    const columnKeys = tableCols.map(({ key }) => key);
    this.tableColumns = tableCols;
    this.sortableColumns = tableCols.filter((col) => col.isSortable);
    this.cardTitleKey = tableCols.find((col) => !col.indicatorIcon)?.key ?? '';
    this.columnKeys = columnKeys;
  }
  /** 'cards' renders each row as a card on phones, 'rows' as a compact list row; every other width keeps the table. */
  @Input() phoneLayout: 'scroll' | 'cards' | 'rows' = 'scroll';
  @Input() totalItems: number | undefined = undefined;
  /**
   * Phones only: no paginator; the end of the list asks for the next page (`paginationChanged` with the next
   * `pageIndex`). The parent owns the accumulated rows: it appends each page to `dataSource`, rolls its page back and
   * calls `loadMoreFailed()` if a request fails.
   */
  @Input() infiniteScrollOnPhone = false;
  @Output() rowClicked: EventEmitter<TableRow> = new EventEmitter<TableRow>();
  @Output() linkClicked: EventEmitter<TableRow> = new EventEmitter<TableRow>();
  @Output() paginationChanged: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() selectionChanged: EventEmitter<TableRow[]> = new EventEmitter<TableRow[]>();
  @Output() sortingChanged: EventEmitter<Sort> = new EventEmitter<Sort>();

  readonly bp = inject(BreakpointService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /** True while the next page is being fetched (the spinner shows), false after it arrives or fails. */
  loadingMore = false;
  loadFailed = false;
  private observer?: IntersectionObserver;
  private sentinelEl?: HTMLElement;
  private readonly cdr = inject(ChangeDetectorRef);

  get infiniteScroll(): boolean {
    return this.infiniteScrollOnPhone && this.bp.isPhone() && this.totalItems !== undefined;
  }

  get hasMore(): boolean {
    return this.totalItems !== undefined && this._dataSource.length < this.totalItems;
  }

  /** While the next page loads, parents hide this table; the stylesheet keeps it visible so the scroll position holds. */
  @HostBinding('class.keep-visible') get keepVisible(): boolean {
    return this.infiniteScroll && this._dataSource.length > 0;
  }

  /** The sentinel sits at the end of the scrolling rows, so it only comes into view when the user reaches them. */
  @ViewChild('sentinel') set sentinel(ref: ElementRef<HTMLElement> | undefined) {
    this.sentinelEl = ref?.nativeElement;
    if (!this.sentinelEl) {
      this.observer?.disconnect();
      return;
    }
    this.observe();
    this.destroyRef.onDestroy(() => this.observer?.disconnect());
  }

  /** The parent could not load the page it was asked for (it has rolled its page number back): offer a retry. */
  loadMoreFailed(): void {
    this.loadingMore = false;
    this.loadFailed = true;
    this.cdr.markForCheck();
  }

  retryLoadMore(): void {
    this.loadFailed = false;
    this.loadMore();
  }

  private loadMore(): void {
    if (this.loadingMore || this.loadFailed || !this.infiniteScroll || !this.hasMore) return;
    this.loadingMore = true;
    this.cdr.markForCheck();
    this.paginationChanged.emit({
      pageIndex: this.selectedPageIndex + 1,
      previousPageIndex: this.selectedPageIndex,
      pageSize: this.pageSize,
      length: this.totalItems ?? 0,
    });
  }

  /** A new observer each time, so the root follows whichever ancestor is scrolling, and a list that is still short keeps loading. */
  private observe(): void {
    if (!this.sentinelEl?.isConnected) return;
    this.observer?.disconnect();
    this.observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && this.loadMore(), {
      root: scrollParent(this.sentinelEl),
      rootMargin: '0px 0px 320px 0px',
    });
    this.observer.observe(this.sentinelEl);
  }

  private sameStart(rows: TableRow[]): boolean {
    const first = this._dataSource[0]?.['id']?.value;
    return first !== undefined && rows[0]?.['id']?.value === first;
  }

  _dataSource: TableRow[] = [];
  tableColumns: TableColumn[] = [];
  sortableColumns: TableColumn[] = [];
  cardTitleKey = '';
  cardSort: Sort = { active: '', direction: 'asc' };
  selectedIndex: number;
  columnKeys: string[] = [];
  isAllSelected = false;
  isAnySelected = false;
  selectedItems: Set<number> = new Set<number>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tableCols) {
      if (this.bulkEnabled) {
        this.columnKeys.unshift('select');
      }
    } else if (changes.bulkEnabled) {
      if (this.bulkEnabled) {
        if (!this.columnKeys.includes('select')) {
          this.columnKeys.unshift('select');
        }
      } else {
        if (this.columnKeys.includes('select')) {
          const indexToRemove: number = this.columnKeys.indexOf('select');
          this.columnKeys.splice(indexToRemove, 1);
        }
      }
    }
  }

  get cardsActive(): boolean {
    return this.phoneLayout !== 'scroll' && this.bp.isPhone();
  }

  onCardSortColumn(active: string): void {
    this.cardSort = { active, direction: this.cardSort.direction || 'asc' };
    this.sortingChanged.emit(this.cardSort);
  }

  onCardSortDirection(): void {
    this.cardSort = { ...this.cardSort, direction: this.cardSort.direction === 'desc' ? 'asc' : 'desc' };
    this.sortingChanged.emit(this.cardSort);
  }

  cellClicked(selectedRow: TableRow, index: number): void {
    if (index === this.selectedIndex) {
      this.selectedIndex = -1;
    } else {
      this.selectedIndex = index;
    }
    this.rowClicked.emit(selectedRow);
  }

  _linkCallback(e: Event, selectedRow: TableRow): void {
    this.linkClicked.emit(selectedRow);
    e.preventDefault();
  }

  announceSortChange(event: Sort) {
    this.cardSort = event;
    this.sortingChanged.emit(event);
  }

  handlePageEvent(event: PageEvent): void {
    const zeroOffsetPageEvent: PageEvent = event;
    if (event.pageIndex !== undefined) {
      zeroOffsetPageEvent.pageIndex = event.pageIndex + 1;
    }
    if (event.previousPageIndex !== undefined) {
      zeroOffsetPageEvent.previousPageIndex = event.previousPageIndex + 1;
    }
    this.paginationChanged.emit(zeroOffsetPageEvent);
  }

  masterToggle(): void {
    if (this.isAllSelected || this.isAnySelected) {
      this.selectedItems.clear();
    } else {
      for (let index = 0; index < this._dataSource.length; index++) {
        this.selectedItems.add(index);
      }
    }

    this.setSelectedStates();
    this._selectionChanged();
  }

  toggle(index: number): void {
    if (this.selectedItems.has(index)) {
      this.selectedItems.delete(index);
    } else {
      this.selectedItems.add(index);
    }

    this.setSelectedStates();
    this._selectionChanged();
  }

  private setSelectedStates() {
    this.isAllSelected = this.selectedItems.size === this._dataSource.length;
    this.isAnySelected = this.selectedItems.size > 0;
  }

  _selectionChanged(): void {
    this.selectionChanged.emit([...this.selectedItems].map((index) => this._dataSource[index]));
  }
}
